import React, { useRef, useState } from 'react';
import { Form, Button, Alert, ProgressBar } from 'react-bootstrap';
import { DetailedProduct } from '../../../../api/shop/types/productTypes';
import { getImageUrl } from '../../../../utils/imageUrl';
import { uploadMultipleProductImages } from '../../../../utils/shopImageUpload';
import './ProductImageUpload.css';

interface ProductImageUploadProps {
  data: Partial<DetailedProduct>;
  onChange: (updates: Partial<DetailedProduct>) => void;
  isReadOnly?: boolean;
  isSportsNutrition?: boolean;
}

const ProductImageUpload: React.FC<ProductImageUploadProps> = ({
  data,
  onChange,
  isReadOnly = false,
  isSportsNutrition = false,
}) => {
  const fileInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({});
  const [uploadErrors, setUploadErrors] = useState<{ [key: string]: string }>({});
  const [isUploading, setIsUploading] = useState<{ [key: string]: boolean }>({});

  // ── Sports nutrition: upload directly to data.images ──────────────────────

  const handleNutritionFileUpload = async (files: FileList | null) => {
    if (!files || files.length === 0 || isReadOnly) return;

    const fileArray = Array.from(files);
    const existing = data.images || [];

    if (existing.length + fileArray.length > 5) {
      setUploadErrors(prev => ({
        ...prev,
        nutrition: `Maximum 5 images allowed. You can add ${5 - existing.length} more.`,
      }));
      return;
    }

    try {
      setIsUploading(prev => ({ ...prev, nutrition: true }));
      setUploadErrors(prev => ({ ...prev, nutrition: '' }));
      setUploadProgress(prev => ({ ...prev, nutrition: 0 }));

      const productName = data.name || 'product';
      const results = await uploadMultipleProductImages(
        fileArray,
        productName,
        'product',
        (progress) => setUploadProgress(prev => ({ ...prev, nutrition: progress }))
      );

      const successfulKeys: string[] = [];
      const errors: string[] = [];

      results.forEach((result, index) => {
        if (result.success && result.s3Key) {
          successfulKeys.push(result.s3Key);
        } else {
          errors.push(`Image ${index + 1}: ${result.error}`);
        }
      });

      if (successfulKeys.length > 0) {
        onChange({ images: [...existing, ...successfulKeys] });
      }

      if (errors.length > 0) {
        setUploadErrors(prev => ({ ...prev, nutrition: errors.join('\n') }));
      } else {
        setUploadErrors(prev => ({ ...prev, nutrition: '' }));
      }

      if (fileInputRefs.current['nutrition']) {
        fileInputRefs.current['nutrition']!.value = '';
      }
    } catch {
      setUploadErrors(prev => ({ ...prev, nutrition: 'Upload failed. Please try again.' }));
    } finally {
      setIsUploading(prev => ({ ...prev, nutrition: false }));
      setUploadProgress(prev => ({ ...prev, nutrition: 0 }));
    }
  };

  const handleNutritionImageRemove = (index: number) => {
    if (isReadOnly) return;
    const updated = (data.images || []).filter((_, i) => i !== index);
    onChange({ images: updated });
  };

  if (isSportsNutrition) {
    const images = data.images || [];

    return (
      <div>
        <h5 className="card-title mb-3">Product Images</h5>

        {/* Upload Progress */}
        {isUploading['nutrition'] && (
          <div className="mb-3">
            <ProgressBar
              now={uploadProgress['nutrition'] || 0}
              label={`${Math.round(uploadProgress['nutrition'] || 0)}%`}
              variant="primary"
              animated
            />
            <small className="text-muted">Uploading images...</small>
          </div>
        )}

        {/* Upload Errors */}
        {uploadErrors['nutrition'] && (
          <Alert variant="danger" className="mb-3">
            <i className="ri-error-warning-line me-2"></i>
            <pre style={{ margin: 0, fontFamily: 'inherit' }}>{uploadErrors['nutrition']}</pre>
          </Alert>
        )}

        {/* Images grid */}
        {images.length > 0 && (
          <div className="simple-image-grid">
            {images.map((imageKey, index) => (
              <div key={index} className="simple-image-item">
                <img
                  src={getImageUrl(imageKey)}
                  alt={`Product image ${index + 1}`}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = getImageUrl('admin/static-assets/icons/shop-service/product.svg');
                  }}
                />
                {!isReadOnly && (
                  <button
                    type="button"
                    className="simple-delete-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNutritionImageRemove(index);
                    }}
                    title="Remove image"
                  >
                    <i className="ri-close-line"></i>
                  </button>
                )}
              </div>
            ))}

            {!isReadOnly && images.length < 5 && (
              <div
                className={`simple-upload-btn ${isUploading['nutrition'] ? 'uploading' : ''}`}
                onClick={() => !isUploading['nutrition'] && fileInputRefs.current['nutrition']?.click()}
                title={isUploading['nutrition'] ? 'Uploading...' : 'Add more images'}
              >
                {isUploading['nutrition'] ? (
                  <div className="spinner-border spinner-border-sm" role="status"></div>
                ) : (
                  <i className="ri-add-line"></i>
                )}
              </div>
            )}
          </div>
        )}

        {/* Upload area when no images */}
        {!isReadOnly && images.length === 0 && !isUploading['nutrition'] && (
          <div
            className="simple-upload-zone"
            onClick={() => fileInputRefs.current['nutrition']?.click()}
          >
            <i className="ri-upload-cloud-2-line"></i>
            <p>Click to upload product images</p>
            <small className="text-muted">Upload up to 5 images (Max 5MB each)</small>
          </div>
        )}

        <input
          ref={(el) => (fileInputRefs.current['nutrition'] = el)}
          type="file"
          multiple
          accept="image/*"
          onChange={(e) => handleNutritionFileUpload(e.target.files)}
          style={{ display: 'none' }}
          disabled={isReadOnly || isUploading['nutrition']}
        />
      </div>
    );
  }

  // ── Default: color-based upload (existing behaviour) ──────────────────────

  const availableColors = data.variants
    ? Array.from(new Set(data.variants.map(v => v.attributes.color)))
    : [];

  const handleFileUpload = async (color: string, files: FileList | null) => {
    if (!files || files.length === 0 || isReadOnly) return;

    const fileArray = Array.from(files);
    const currentColorImages = data.colorImages || {};
    const existingCount = (currentColorImages[color] || []).length;

    if (existingCount + fileArray.length > 5) {
      setUploadErrors(prev => ({
        ...prev,
        [color]: `Maximum 5 images allowed per color. You can add ${5 - existingCount} more images.`,
      }));
      return;
    }

    try {
      setIsUploading(prev => ({ ...prev, [color]: true }));
      setUploadErrors(prev => ({ ...prev, [color]: '' }));
      setUploadProgress(prev => ({ ...prev, [color]: 0 }));

      const productName = data.name || 'product';
      const results = await uploadMultipleProductImages(
        fileArray,
        productName,
        color,
        (progress) => setUploadProgress(prev => ({ ...prev, [color]: progress }))
      );

      const successfulUploads: string[] = [];
      const errors: string[] = [];

      results.forEach((result, index) => {
        if (result.success && result.s3Key) {
          successfulUploads.push(result.s3Key);
        } else {
          errors.push(`Image ${index + 1}: ${result.error}`);
        }
      });

      if (successfulUploads.length > 0) {
        const updatedColorImages = {
          ...currentColorImages,
          [color]: [...(currentColorImages[color] || []), ...successfulUploads],
        };
        onChange({ colorImages: updatedColorImages });
      }

      if (errors.length > 0) {
        setUploadErrors(prev => ({ ...prev, [color]: errors.join('\n') }));
      } else {
        setUploadErrors(prev => ({ ...prev, [color]: '' }));
      }

      if (fileInputRefs.current[color]) {
        fileInputRefs.current[color]!.value = '';
      }
    } catch {
      setUploadErrors(prev => ({ ...prev, [color]: 'Upload failed. Please try again.' }));
    } finally {
      setIsUploading(prev => ({ ...prev, [color]: false }));
      setUploadProgress(prev => ({ ...prev, [color]: 0 }));
    }
  };

  const handleImageRemove = (color: string, imageIndex: number) => {
    if (isReadOnly) return;
    const currentColorImages = data.colorImages || {};
    const updatedImages = (currentColorImages[color] || []).filter((_, i) => i !== imageIndex);
    onChange({ colorImages: { ...currentColorImages, [color]: updatedImages } });
  };

  if (availableColors.length === 0) {
    return (
      <div>
        <h5 className="card-title mb-3">Product Images</h5>
        <Alert variant="info">
          <i className="ri-information-line me-2"></i>
          Please select colors in the "Customize Variants" section first to enable image upload.
        </Alert>
      </div>
    );
  }

  return (
    <div>
      {availableColors.map(color => {
        const colorImages = data.colorImages?.[color] || [];

        return (
          <div key={color} className="simple-color-section mb-4">
            <h5 className="simple-color-title mb-3">
              Upload Images for: {color}
              {colorImages.length > 0 && (
                <span className="text-muted ms-2">({colorImages.length}/5)</span>
              )}
            </h5>

            {isUploading[color] && (
              <div className="mb-3">
                <ProgressBar
                  now={uploadProgress[color] || 0}
                  label={`${Math.round(uploadProgress[color] || 0)}%`}
                  variant="primary"
                  animated
                />
                <small className="text-muted">Uploading images...</small>
              </div>
            )}

            {uploadErrors[color] && (
              <Alert variant="danger" className="mb-3">
                <i className="ri-error-warning-line me-2"></i>
                <pre style={{ margin: 0, fontFamily: 'inherit' }}>{uploadErrors[color]}</pre>
              </Alert>
            )}

            {colorImages.length > 0 && (
              <div className="simple-image-grid">
                {colorImages.map((imageUrl, index) => (
                  <div key={index} className="simple-image-item">
                    <img
                      src={getImageUrl(imageUrl)}
                      alt={`${color} variant ${index + 1}`}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = getImageUrl('admin/static-assets/icons/shop-service/product.svg');
                      }}
                    />
                    {!isReadOnly && (
                      <button
                        type="button"
                        className="simple-delete-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleImageRemove(color, index);
                        }}
                        title="Remove image"
                      >
                        <i className="ri-close-line"></i>
                      </button>
                    )}
                  </div>
                ))}

                {!isReadOnly && colorImages.length < 5 && (
                  <div
                    className={`simple-upload-btn ${isUploading[color] ? 'uploading' : ''}`}
                    onClick={() => !isUploading[color] && fileInputRefs.current[color]?.click()}
                    title={isUploading[color] ? 'Uploading...' : 'Add more images'}
                  >
                    {isUploading[color] ? (
                      <div className="spinner-border spinner-border-sm" role="status"></div>
                    ) : (
                      <i className="ri-add-line"></i>
                    )}
                  </div>
                )}
              </div>
            )}

            {!isReadOnly && colorImages.length === 0 && !isUploading[color] && (
              <div
                className="simple-upload-zone"
                onClick={() => !isUploading[color] && fileInputRefs.current[color]?.click()}
              >
                <i className="ri-upload-cloud-2-line"></i>
                <p>Click to upload images for {color}</p>
                <small className="text-muted">Upload up to 5 images (Max 5MB each)</small>
              </div>
            )}

            <input
              ref={(el) => (fileInputRefs.current[color] = el)}
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => handleFileUpload(color, e.target.files)}
              style={{ display: 'none' }}
              disabled={isReadOnly || isUploading[color]}
            />
          </div>
        );
      })}
    </div>
  );
};

export default ProductImageUpload;
